import { Base64 } from 'src/services/encode-utilities';
import { PlayerAuthParamsState, PlayerParamsState } from 'src/types/playerParams.type';

export type PlayerParamsAuthMapItem = {
  stateKey: keyof PlayerAuthParamsState;
  aliases: string[];
  transform?: (v: string) => any;
};

export type PlayerParamMapItem = {
  stateKey: keyof PlayerParamsState;
  aliases: string[];
  transform?: (v: string) => any;
};

export const authenticationParamMap: PlayerParamsAuthMapItem[] = [
  { stateKey: 'token', aliases: ['token', 'TOKEN', 'access_token'] },
  { stateKey: 'randomId', aliases: ['randomId', 'random_id', 'uuid'] },
];

export const playerParamMap: PlayerParamMapItem[] = [
  { stateKey: 'scheduleId', aliases: ['scheduleId'] },
  { stateKey: 'subjectSlug', aliases: ['subjectSlug'] },
  { stateKey: 'batchSlug', aliases: ['batchSlug'] },
  { stateKey: 'batchSubjectId', aliases: ['batchSubjectId'] },
  { stateKey: 'src', aliases: ['src', 'url'], transform: (v: string) => v },
  { stateKey: 'src', aliases: ['encoded_url'], transform: (v: string) => Base64.decode(v) },
  { stateKey: 'type', aliases: ['type'] },
  {
    stateKey: 'drmVendor',
    aliases: ['drmVendor'],
    transform: (v: string) => {
      try {
        return v ? JSON.parse(v) : '';
      } catch {
        return '';
      }
    },
  },
  { stateKey: 'playingSource', aliases: ['playingSource'] },
  { stateKey: 'entityId', aliases: ['entityId'] },
  { stateKey: 'skillsToken', aliases: ['skillsToken'] },
  { stateKey: 'contentId', aliases: ['contentId'] },
  { stateKey: 'khazanaProgramId', aliases: ['programId'] },
  { stateKey: 'videoId', aliases: ['videoId', 'video_id'] },
  { stateKey: 'isWebinar', aliases: ['isWebinar'], transform: (v: string) => v === 'true' },
  { stateKey: 'typeId', aliases: ['typeId'] },
  { stateKey: 'defaultPaused', aliases: ['default_paused'], transform: JSON.parse },
  { stateKey: 'defaultMuted', aliases: ['default_muted'], transform: JSON.parse },
  { stateKey: 'loop', aliases: ['loop'], transform: JSON.parse },
  { stateKey: 'disableSeek', aliases: ['disableSeek'], transform: JSON.parse },
  { stateKey: 'controls', aliases: ['hide_controls'], transform: (v: string) => !JSON.parse(v) },
  { stateKey: 'isUnderMaintenance', aliases: ['isUnderMaintenance'], transform: JSON.parse },
  { stateKey: 'showHeader', aliases: ['is_header_enabled'], transform: JSON.parse },
  { stateKey: 'showProducts', aliases: ['is_products_enabled'], transform: JSON.parse },
  { stateKey: 'isBackButtonEnable', aliases: ['back_button'], transform: JSON.parse },
  { stateKey: 'isThreeDotsEnable', aliases: ['three_dots'], transform: JSON.parse },
  { stateKey: 'isPollEnabled', aliases: ['is_poll_enabled'], transform: JSON.parse },
  { stateKey: 'isComplete', aliases: ['isComplete'], transform: (v: string) => v === 'true' },
  { stateKey: 'cameFrom', aliases: ['came_from', 'cameFrom'] },
  { stateKey: 'isWebview', aliases: ['webview'], transform: (v: string) => v === 'true' },
  { stateKey: 'defaultEnabledSlideModeFeature', aliases: ['default_enabled_feature'] },
  { stateKey: 'onlyEnabledSlideModeFeature', aliases: ['only_enabled_feature'] },
  { stateKey: 'isEmojiEnabled', aliases: ['emoji_enabled'], transform: JSON.parse },
  { stateKey: 'startTime', aliases: ['trim_start', 'doubtTime'], transform: JSON.parse },
  { stateKey: 'endTime', aliases: ['trim_end'], transform: JSON.parse },
  { stateKey: 'entryPoint', aliases: ['entryPoint'] },
  { stateKey: 'startTimeStamp', aliases: ['startTimeStamp'], transform: Number },
  { stateKey: 'endTimeStamp', aliases: ['endTimeStamp'], transform: Number },
  { stateKey: 'childId', aliases: ['childId'] },
  { stateKey: 'parentId', aliases: ['parentId'] },
  { stateKey: 'vType', aliases: ['vType'] },
  { stateKey: 'secondaryParentId', aliases: ['secondaryParentId'] },
  { stateKey: 'secondaryChildId', aliases: ['secondaryChildId'] },
  { stateKey: 'isPPJEnabled', aliases: ['isPPJEnabled'], transform: (v: string) => v === 'true' },
  { stateKey: 'isFromCjr', aliases: ['isFromCjr'], transform: (v: string) => v === 'true' },
  { stateKey: 'metadata', aliases: ['metadata'] },
  { stateKey: 'typeKey', aliases: ['typeKey'] },
  { stateKey: 'reportTypeKey', aliases: ['reportTypeKey'] },
  { stateKey: 'isCommentsEnabled', aliases: ['is_comments_enabled'], transform: JSON.parse },
  { stateKey: 'isReportEnabled', aliases: ['is_report_enabled'], transform: JSON.parse },
  { stateKey: 'isRatingEnabled', aliases: ['is_rating_enabled'], transform: JSON.parse },
  { stateKey: 'metadata', aliases: ['metadata'] },
  { stateKey: 'playlistId', aliases: ['playlistId'] },
  { stateKey: 'testId', aliases: ['testId'] },
  { stateKey: 'backUrl', aliases: ['back_url', 'backUrl'] },
  {
    stateKey: 'isCopilotNotification',
    aliases: ['isCopilotNotification'],
    transform: (v: string) => v === 'true',
  },
];

export const initialPlayerParamsState: PlayerParamsState = {
  scheduleId: '',
  subjectSlug: '',
  batchSlug: '',
  batchSubjectId: '',
  src: '',
  drmVendor: '',
  playingSource: '',
  entityId: '',
  skillsToken: '',
  disableSeek: false,
  type: '',
  defaultPaused: false,
  defaultMuted: false,
  loop: false,
  controls: true,
  isUnderMaintenance: false,
  contentId: '',
  khazanaProgramId: '',
  videoId: '',
  startTime: undefined,
  endTime: undefined,
  cameFrom: '',
  isWebview: false,
  isCjrWebview: false,
  clientType: undefined,
  deviceType: undefined,
  showHeader: true,
  showProducts: true,
  isBackButtonEnable: true,
  isThreeDotsEnable: true,
  isPollEnabled: true,
  entryPoint: '',
  startTimeStamp: undefined,
  endTimeStamp: undefined,
  isWebinar: false,
  typeId: '',
  defaultEnabledSlideModeFeature: '',
  onlyEnabledSlideModeFeature: '',
  isEmojiEnabled: true,
  isComplete: false,
  childId: '',
  parentId: '',
  secondaryParentId: '',
  secondaryChildId: '',
  vType: '',
  isPPJEnabled: false,
  isFromCjr: false,
  cameFromQBG: false,
  isWebRTCVideoProcessing: false,
  typeKey: '',
  reportTypeKey: '',
  isCommentsEnabled: false,
  isReportEnabled: false,
  isRatingEnabled: false,
  metadata: '',
  playlistId: '',
  backUrl: '',
  isCopilotNotification: false,
};
